<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import SearchHighlightText from '@/components/createdUi/SearchHighlightText.vue'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { ITreeSelectStore } from '@/types/ITreeSelectStore'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

/**
 * UniversalTreeMultiSelect - компонент МНОЖЕСТВЕННОГО выбора с кнопками подтверждения.
 */

interface Props {
  fieldKey: string
  module?: IUniversalTreeSelectModule
  store: ITreeSelectStore
  modelValue: (string | number)[]
  placeholder?: string
  onlyLeaves?: boolean
  collapsedByDefault?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsedByDefault: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: (string | number)[]): void
  (e: 'change', items: { id: string | number; label: string }[]): void
  (e: 'close'): void // Событие для закрытия меню
}>()

// --- Внутреннее состояние ---
const search = ref('')
const searchDebounced = refDebounced(search, 300)
const open = ref<any[]>([])
const observerElement = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

// Временное хранилище выбранных ID (буфер) — Set для O(1) проверок
const tempSelectedIds = ref<Set<string | number>>(new Set())
const isSelectedMenuOpen = ref(false)

const selectedLabelsMap = ref<Record<string, string>>({})

// --- Вычисления ---
const fieldKey = props.fieldKey as string

const displayItems = computed<IFilterChoiceItem[]>(() => {
  const rawItems = searchDebounced.value
    ? (props.store.treeSearchChoices[props.fieldKey] || [])
    : (props.store.treeChoices[props.fieldKey] || [])

  if (!rawItems.length) return []
  return rawItems
})

/**
 * Фиксация высоты карточки при первом полном рендере списка.
 */
const rootCardRef = ref<{ $el: HTMLElement } | null>(null)
const lockedHeight = ref<number | null>(null)

watch(displayItems, items => {
  if (lockedHeight.value !== null || !items.length) return
  nextTick(() => {
    const el = rootCardRef.value?.$el
    if (el) {
      lockedHeight.value = Math.round(el.getBoundingClientRect().height)
    }
  })
}, { immediate: true })

/**
 * Кеш: nodeId -> массив ID всех листьев этого узла.
 */
const leafIdsByNode = computed<Map<string | number, (string | number)[]>>(() => {
  const map = new Map<string | number, (string | number)[]>()

  const collect = (item: IFilterChoiceItem): (string | number)[] => {
    if (!item.children?.length) {
      const leafIds = [item.id]
      map.set(item.id, leafIds)
      return leafIds
    }
    const leafIds = item.children.flatMap(child => collect(child))
    map.set(item.id, leafIds)
    return leafIds
  }

  for (const item of displayItems.value) {
    collect(item)
  }

  return map
})

/**
 * Плоская карта названий ID -> label для O(1) поиска.
 */
const labelsMap = computed(() => {
  const map = new Map<string | number, string>()
  const collect = (nodes: IFilterChoiceItem[]) => {
    for (const node of nodes) {
      map.set(node.id, node.label)
      if (node.children?.length) {
        collect(node.children)
      }
    }
  }
  collect(displayItems.value)
  return map
})

/**
 * Реактивное состояние выбора для каждого узла.
 * Использует key-level отслеживание Vue 3 для точечных обновлений DOM.
 */
const selectionStateByNode = reactive(new Map<string | number, 'all' | 'partial' | 'none'>())

/**
 * Карта соответствия дочерний_узел -> родительский_узел для O(1) поиска предков.
 */
const parentIdMap = computed(() => {
  const map = new Map<string | number, string | number>()
  const traverse = (node: IFilterChoiceItem, parentId?: string | number) => {
    if (parentId !== undefined) {
      map.set(node.id, parentId)
    }
    if (node.children) {
      for (const child of node.children) {
        traverse(child, node.id)
      }
    }
  }
  displayItems.value.forEach(node => traverse(node))
  return map
})

/**
 * Полный пересчет состояний выбора (для инициализации и смены дерева).
 */
const rebuildAllSelectionStates = () => {
  selectionStateByNode.clear()
  const selected = tempSelectedIds.value

  for (const [nodeId, leafIds] of leafIdsByNode.value) {
    let state: 'all' | 'partial' | 'none' = 'none'
    if (leafIds.length > 0) {
      let count = 0
      for (const id of leafIds) {
        if (selected.has(id) || selected.has(String(id)) || selected.has(Number(id))) count++
      }
      if (count === leafIds.length) state = 'all'
      else if (count > 0) state = 'partial'
    }
    selectionStateByNode.set(nodeId, state)
  }
}

/**
 * Точечный пересчет статуса для предков измененного узла.
 */
const updateAncestorStates = (nodeId: string | number) => {
  let currentId: string | number | undefined = nodeId
  while (currentId !== undefined) {
    const leafIds = leafIdsByNode.value.get(currentId) || []
    let count = 0
    for (const lid of leafIds) {
      if (tempSelectedIds.value.has(lid) || tempSelectedIds.value.has(String(lid)) || tempSelectedIds.value.has(Number(lid))) count++
    }

    let state: 'all' | 'partial' | 'none' = 'none'
    if (leafIds.length > 0) {
      if (count === leafIds.length) state = 'all'
      else if (count > 0) state = 'partial'
    }

    if (selectionStateByNode.get(currentId) !== state) {
      selectionStateByNode.set(currentId, state)
    }
    currentId = parentIdMap.value.get(currentId)
  }
}

const selectedItemsData = computed(() => {
  return [...tempSelectedIds.value].map(id => ({
    id,
    name: selectedLabelsMap.value[String(id)]
      || labelsMap.value.get(id)
      || String(id),
  }))
})

const removeItem = (id: string | number) => {
  const newSet = new Set(tempSelectedIds.value)
  newSet.delete(id)
  newSet.delete(String(id))
  newSet.delete(Number(id))
  tempSelectedIds.value = newSet
  if (newSet.size === 0) {
    isSelectedMenuOpen.value = false
  }
  updateAncestorStates(id)
}

const removeAllItems = () => {
  tempSelectedIds.value = new Set()
  isSelectedMenuOpen.value = false
  rebuildAllSelectionStates()
}

const itemProps = (item: any) => {
  const state = selectionStateByNode.get(item.id)
  return {
    class: {
      'v-list-item--active': state === 'all' || state === 'partial',
    },
  }
}

/**
 * Определяет, есть ли вложенность в данных
 */
const hasNestedItems = computed(() =>
  displayItems.value.some(item => item.children?.length),
)

/**
 * Набор ID корневых элементов для O(1) проверки
 */
const rootItemIds = computed<Set<string | number>>(() =>
  new Set(displayItems.value.map(i => i.id)),
)

const isRootItem = (item: IFilterChoiceItem) => rootItemIds.value.has(item.id)

// --- Слежение за внешним значением ---
watch(() => props.modelValue, newVal => {
  tempSelectedIds.value = new Set(newVal || [])
  rebuildAllSelectionStates()
}, { immediate: true, deep: true })

/**
 * Переключение чекбокса.
 */
const toggleNodeSelect = (item: IFilterChoiceItem) => {
  const isLeaf = !item.children || item.children.length === 0
  const newSet = new Set(tempSelectedIds.value)
  const hasSelected = (id: string | number) => {
    return newSet.has(id) || newSet.has(String(id)) || newSet.has(Number(id))
  }
  const deleteSelected = (id: string | number) => {
    newSet.delete(id)
    newSet.delete(String(id))
    newSet.delete(Number(id))
  }

  if (isLeaf) {
    if (hasSelected(item.id)) {
      deleteSelected(item.id)
    } else {
      newSet.add(item.id)
      selectedLabelsMap.value[String(item.id)] = item.label
    }
  } else {
    const leafIds = leafIdsByNode.value.get(item.id) || []
    const allSelected = selectionStateByNode.get(item.id) === 'all'

    if (allSelected) {
      for (const id of leafIds) deleteSelected(id)
    } else {
      for (const id of leafIds) {
        newSet.add(id)
        // Сохраняем лейбл для каждого дочернего элемента
        const label = labelsMap.value.get(id)
        if (label) {
          selectedLabelsMap.value[String(id)] = label
        }
      }

      // Автоматически раскрываем этот узел и его подузлы
      const collectSubGroupIds = (node: IFilterChoiceItem): (string | number)[] => {
        if (!node.children?.length) return []
        return [node.id, ...node.children.flatMap(child => collectSubGroupIds(child))]
      }
      const toExpand = collectSubGroupIds(item)
      const newOpen = new Set(open.value)
      for (const id of toExpand) {
        newOpen.add(id)
      }
      open.value = [...newOpen]
    }
  }

  tempSelectedIds.value = newSet
  rebuildAllSelectionStates()
}

const onSave = () => {
  const ids = [...tempSelectedIds.value]

  const objectsToEmit = ids.map(id => ({
    id,
    label: selectedLabelsMap.value[String(id)]
      || labelsMap.value.get(id)
      || String(id),
  }))

  emit('update:modelValue', ids)
  emit('change', objectsToEmit)
  emit('close')
}

const onCancel = () => {
  tempSelectedIds.value = new Set(props.modelValue || [])
  emit('close')
}

const handleInfiniteScroll = async () => {
  const cursor = props.store.treeCursors[props.fieldKey]
  const isLoading = props.store.treeLoading[props.fieldKey]

  if (cursor && !isLoading) {
    let cursorValue: string | null
    try {
      cursorValue = new URL(cursor).searchParams.get('cursor') || cursor
    } catch {
      cursorValue = cursor
    }

    await props.store.fetchTreeChoices(
      props.fieldKey,
      searchDebounced.value,
      cursorValue,
      props.module,
    )
  }
}

// --- Логика отображения дерева как плоского списка ---

const isChildMatchingSearch = (node: IFilterChoiceItem, searchTerm: string): boolean => {
  if (!node.children?.length) return false
  return node.children.some(child =>
    child.label.toLowerCase().includes(searchTerm.toLowerCase())
    || isChildMatchingSearch(child, searchTerm),
  )
}

const getVisibleNodes = (
  nodes: IFilterChoiceItem[],
  expandedKeys: (string | number)[],
  searchTerm = '',
  depth = 0,
  parentMatchesSearch = false,
): any[] => {
  const visible: any[] = []

  nodes.forEach(node => {
    const nodeSelfMatches = Boolean(
      searchTerm && node.label.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    const nodeMatches = !searchTerm
      || parentMatchesSearch
      || nodeSelfMatches
      || isChildMatchingSearch(node, searchTerm)

    if (nodeMatches) {
      const isLeaf = !node.children || node.children.length === 0
      visible.push({
        ...node,
        depth,
        isLeaf,
      })

      const isParentOrSelfMatching = parentMatchesSearch || nodeSelfMatches
      const shouldShowChildren = expandedKeys.includes(node.id)
      if (shouldShowChildren && node.children?.length) {
        visible.push(
          ...getVisibleNodes(
            node.children,
            expandedKeys,
            searchTerm,
            depth + 1,
            isParentOrSelfMatching,
          ),
        )
      }
    }
  })

  return visible
}

const visibleNodes = computed(() => {
  return getVisibleNodes(displayItems.value, open.value, searchDebounced.value)
})

const toggleNode = (id: string | number) => {
  const idx = open.value.indexOf(id)
  if (idx === -1) open.value = [...open.value, id]
  else open.value = open.value.filter(v => v !== id)
}

watch(searchDebounced, async val => {
  await props.store.fetchTreeChoices(
    props.fieldKey,
    val || '',
    undefined,
    props.module,
  )
  if (val && displayItems.value.length > 0) {
    const ids: (string | number)[] = []
    const walk = (items: IFilterChoiceItem[]) => {
      for (const node of items) {
        if (node.children?.length && isChildMatchingSearch(node, val)) {
          ids.push(node.id)
          walk(node.children)
        }
      }
    }
    walk(displayItems.value)
    open.value = ids
  } else {
    open.value = []
  }
})
watch(displayItems, items => {
  if (!items?.length) return

  if (!props.collapsedByDefault) {
    const idsWithChildren: (string | number)[] = []

    const collect = (nodes: IFilterChoiceItem[]) => {
      for (const node of nodes) {
        if (node.children && node.children.length > 0) {
          idsWithChildren.push(node.id)
          collect(node.children)
        }
      }
    }

    collect(items)
    open.value = idsWithChildren
  }

  rebuildAllSelectionStates()
}, { immediate: true })

onMounted(async () => {
  if (!props.store.treeChoices[props.fieldKey]?.length) {
    await props.store.fetchTreeChoices(
      props.fieldKey,
      undefined,
      undefined,
      props.module,
    )
  }

  observer = new IntersectionObserver(async ([entry]) => {
    if (entry.isIntersecting) await handleInfiniteScroll()
  }, { threshold: 0.1 })

  if (observerElement.value) observer.observe(observerElement.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <VCard
    ref="rootCardRef"
    class="universal-tree-select-container d-flex flex-column"
    elevation="8"
  >
    <VSheet class="pa-4 flex-grow-0">
      <VTextField
        v-model="search"
        :placeholder="placeholder || 'Поиск...'"
        variant="outlined"
        rounded="pill"
        class="search-field"
        :append-inner-icon="search ? undefined : 'ri-search-line'"
        hide-details
        clearable
        autofocus
        autocomplete="off"
      />
    </VSheet>

    <!-- Оверлей выбранных элементов (при наведении) -->
    <div class="px-4 pb-2" :style="{ visibility: tempSelectedIds.size === 0 ? 'hidden' : 'visible' }">
      <VMenu
        v-model="isSelectedMenuOpen"
        open-on-hover
        :close-on-content-click="false"
        close-delay="200"
        location="bottom start"
        offset="8"
        content-class="date-picker-menu"
      >
        <template #activator="{ props: menuProps }">
          <span
            v-bind="menuProps"
            class="text-body-1 text-medium-emphasis cursor-pointer d-inline-block"
          >
            Выбрано: {{ tempSelectedIds.size }}
          </span>
        </template>

        <CustomSelectedItems
          :items="selectedItemsData"
          :title="`Выбрано ${tempSelectedIds.size}:`"
          hide-avatar
          @remove="removeItem"
          @remove-all="removeAllItems"
        />
      </VMenu>
    </div>

    <VDivider />

    <VSheet class="tree-view-content flex-grow-1 d-flex flex-column">
      <div
        v-if="store.treeLoading[fieldKey]"
        class="d-flex align-center justify-center flex-grow-1 py-12"
        style="min-height: 200px;"
      >
        <AnimatedLogoLoader />
      </div>

      <template v-else>
        <VList class="custom-treeview px-2">
          <div
            v-for="item in visibleNodes"
            :key="item.id"
            class="v-list-item"
            :class="{
              'v-list-item--active': selectionStateByNode.get(item.id) === 'all',
              'tree-item--selected': selectionStateByNode.get(item.id) === 'all',
              'tree-item--leaf': item.isLeaf,
            }"
            :style="{ '--indent-level': item.depth }"
          >
            <span class="v-list-item__overlay" />
            <span class="v-list-item__underlay" />
            <div class="v-list-item__prepend">
              <div class="v-list-item-action v-list-item-action--start">
                <!-- Скрыть toggle для корневых элементов-dividers -->
                <div v-if="hasNestedItems && isRootItem(item)" />
                <VIconBtn
                  v-else-if="!item.isLeaf"
                  icon="ri-play-fill"
                  class="expand-btn"
                  variant="text"
                  :class="{ expanded: open.includes(item.id) }"
                  @click.stop="toggleNode(item.id)"
                />
                <div v-else class="node-leaf-spacer" />
              </div>
              <div class="v-list-item__spacer" />
            </div>

            <div class="v-list-item__content">
              <div class="v-list-item-title">
                <!-- Корневая группа: CustomDivider + кликабельная стрелка справа -->
                <div
                  v-if="hasNestedItems && isRootItem(item)"
                  class="d-flex align-center py-1 cursor-pointer w-100 root-divider-row"
                  @click.stop="toggleNode(item.id)"
                >
                  <VIconBtn
                    icon="ri-play-fill"
                    class="expand-btn me-2"
                    variant="text"
                    :class="{ expanded: open.includes(item.id) }"
                  />
                  <div class="flex-grow-1 min-w-0">
                    <CustomDivider :title="item.label" />
                  </div>
                </div>
                <!-- Текст для всех остальных (листья и промежуточные узлы) без чекбоксов -->
                <div
                  v-else
                  class="d-flex align-center py-1 cursor-pointer w-100"
                  @click.stop="toggleNodeSelect(item)"
                >
                  <div class="text-body-1 text-high-emphasis text-wrap">
                    <SearchHighlightText
                      :text="item.label"
                      :query="searchDebounced"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </VList>

        <div ref="observerElement" class="d-flex justify-center align-center py-4" style="min-height: 60px;">
          <span v-if="!store.treeCursors[fieldKey] && displayItems.length > 0" class="text-caption text-disabled">
            Конец классификатора
          </span>
        </div>
      </template>
    </VSheet>

    <VDivider />

    <div class="select-menu-footer">
      <VBtn
        color="secondary"
        variant="text"
        class="w-100 w-sm-auto"
        @click="onCancel"
      >
        Отмена
      </VBtn>
      <VBtn
        color="primary"
        variant="elevated"
        class="w-100 w-sm-auto"
        @click="onSave"
      >
        Применить
      </VBtn>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.universal-tree-select-container {
  max-height: 60vh;

  @media (max-width: 600px) {
    max-height: 80vh !important;
    height: 100% !important;
  }

  .text-wrap {
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }
}

@media (max-width: 600px) {
  :global(.v-overlay__content.entity-form-menu.tree-select-menu) {
    max-height: 80vh !important;
    height: 80vh !important;
    max-width: calc(100vw - 32px) !important;
    width: calc(100vw - 32px) !important;
    left: 16px !important;
  }
}

.tree-view-content {
  overflow-y: auto;
  min-height: 0; /* Важно для flex-контейнера */
}

.root-divider-row > .min-w-0,
.root-divider-row :deep(.custom-divider) {
  min-width: 0;
}

.root-divider-row :deep(.custom-divider .text) {
  min-width: 0;
  white-space: normal;
  word-break: break-word;
}

.root-divider-row :deep(.custom-divider .line.left),
.root-divider-row :deep(.custom-divider .line.right) {
  display: none;
}

:deep(.v-list-item:has(.root-divider-row)) {
  .v-list-item__prepend {
    display: none !important;
  }
}

.tree-view-content :deep(.v-list-group__items) {
  transition: none !important;
}
</style>
